import { useMemo } from 'react';

import { DocumentNode } from '@apollo/client';
import { type FragmentDefinitionNode, Kind, parse } from 'graphql';

import {
  CollectionEntryFragment,
  CollectionEntryFragmentDoc,
  DefaultErrorFragment,
  IdentityRefFragment,
  composeDocument,
  getTypeOrNull,
} from '@spotify/confidence-graphql';

import {
  type CollectionEntryExtensionOptions,
  useCollectionEntryExtensions,
} from './extensions';

export type CollectionEntryResource = NonNullable<
  CollectionEntryFragment['resource']
> & {
  owner?: IdentityRefFragment | DefaultErrorFragment | null;
  labels?: Array<{ key: string; value?: string | null }>;
};

export const getResourceOwner = (
  resource?: CollectionEntryResource,
): IdentityRefFragment | undefined => {
  if (!resource?.owner) return undefined;
  return getTypeOrNull(resource.owner) ?? undefined;
};

export const getResourceLabels = (
  resource?: CollectionEntryResource,
): Array<{ key: string; value?: string | null }> | undefined => {
  return resource?.labels;
};

const getFragmentDef = (
  doc: DocumentNode,
): FragmentDefinitionNode | undefined =>
  doc.definitions.find(
    (d): d is FragmentDefinitionNode => d.kind === Kind.FRAGMENT_DEFINITION,
  );

export const composeCollectionEntryFragments = (
  extensions: CollectionEntryExtensionOptions[],
): { fragment: DocumentNode; fragmentName: string } | undefined => {
  const withFragments = extensions.filter(
    (
      ext,
    ): ext is CollectionEntryExtensionOptions & { fragment: DocumentNode } =>
      !!ext.fragment,
  );

  if (withFragments.length === 0) return undefined;

  const fragmentInfos = withFragments
    .map(ext => {
      const def = getFragmentDef(ext.fragment);
      if (!def) return undefined;
      return {
        fragmentName: def.name.value,
        typeName: def.typeCondition.name.value,
        doc: ext.fragment,
      };
    })
    .filter((f): f is NonNullable<typeof f> => !!f);

  if (fragmentInfos.length === 0) return undefined;

  const inlineSpreads = fragmentInfos
    .map(
      ({ typeName, fragmentName }) =>
        `... on ${typeName} { ...${fragmentName} }`,
    )
    .join('\n');

  const wrapperDoc = parse(`
    fragment CollectionPageEntry on CollectionsV1CollectionEntry {
      ...CollectionEntry
      resource {
        ${inlineSpreads}
      }
    }
  `);

  const composed = composeDocument(wrapperDoc, [
    CollectionEntryFragmentDoc,
    ...fragmentInfos.map(f => f.doc),
  ]);

  return { fragment: composed, fragmentName: 'CollectionPageEntry' };
};

export const useComposedCollectionEntryFragments = () => {
  const extensions = useCollectionEntryExtensions();
  return useMemo(
    () => composeCollectionEntryFragments(extensions),
    [extensions],
  );
};
